import { useEffect, useState } from "react";
import { api, formatApiError } from "@/lib/api";
import { useSettings } from "@/context/SettingsContext";
import { toast } from "sonner";
import { Save, RotateCcw, Info } from "lucide-react";

const RULE_DEFAULTS = {
    free_shipping_enabled: true,
    free_shipping_threshold: 1499,
    ship_fallback_weight_g: 500,
    ship_std_0_500_g: 70,
    ship_std_501_1000_g: 100,
    ship_std_1001_2000_g: 140,
    ship_std_extra_per_kg: 60,
    ship_rod_base: 250,
    ship_rod_extra_per_kg: 80,
    ship_rod_len_120: 100,
    ship_rod_len_180_extra: 200,
    ship_heavy_base: 180,
    ship_heavy_extra_per_kg: 80,
    ship_volumetric_divisor: 5000,
    ship_fallback_charge: 99,
};

const NUMERIC_KEYS = Object.keys(RULE_DEFAULTS).filter((k) => k !== "free_shipping_enabled");

function validate(form) {
    for (const k of NUMERIC_KEYS) {
        const v = form[k];
        if (v === "" || v === null || v === undefined) continue;
        const n = Number(v);
        if (Number.isNaN(n)) return `${k} must be a number.`;
        if (n < 0) return `${k} cannot be negative.`;
        if (k === "ship_volumetric_divisor" && n <= 0) return "Volumetric divisor must be greater than 0.";
    }
    return null;
}

export default function AdminShipping() {
    const { refresh } = useSettings();
    const [s, setS] = useState(null);
    const [saving, setSaving] = useState(false);
    const [resetting, setResetting] = useState(false);
    const [error, setError] = useState("");

    useEffect(() => {
        api.get("/admin/settings").then((r) => setS(r.data));
    }, []);

    if (!s) return <div className="text-white/55">Loading…</div>;

    const set = (k, v) => setS({ ...s, [k]: v });

    const save = async () => {
        const err = validate(s);
        if (err) {
            setError(err);
            toast.error(err);
            return;
        }
        setSaving(true);
        setError("");
        try {
            const payload = {
                cod_enabled: !!s.cod_enabled,
                free_shipping_enabled: !!s.free_shipping_enabled,
                free_shipping_threshold: Number(s.free_shipping_threshold || 0),
            };
            for (const k of NUMERIC_KEYS) {
                if (k === "free_shipping_threshold") continue;
                if (s[k] === "" || s[k] === null || s[k] === undefined) continue;
                payload[k] = Number(s[k]);
            }
            const { data } = await api.put("/admin/settings", payload);
            setS(data);
            toast.success("Shipping rules saved — checkout will use these immediately.");
            refresh();
        } catch (e) {
            const msg = formatApiError(e?.response?.data?.detail) || e?.message || "Save failed";
            setError(msg);
            toast.error(`Save failed: ${msg}`);
        }
        setSaving(false);
    };

    const resetDefaults = async () => {
        if (!window.confirm("Reset all shipping rules to system defaults? This affects future checkouts only — existing orders stay unchanged.")) return;
        setResetting(true);
        try {
            const { data } = await api.post("/admin/settings/shipping/reset");
            setS(data);
            toast.success("Shipping rules reset to defaults.");
            refresh();
        } catch (e) {
            toast.error("Reset failed");
        }
        setResetting(false);
    };

    return (
        <div className="space-y-5 max-w-4xl" data-testid="admin-shipping-page">
            <div>
                <div className="text-xs uppercase tracking-[0.3em] text-[#00BCE5] font-display font-bold">Operations</div>
                <h1 className="font-display text-3xl font-black uppercase tracking-tight">Shipping Rules</h1>
                <p className="text-white/55 text-sm mt-1">
                    Edit shipping prices below. Backend recalculates every checkout from these rules — old orders keep
                    their original shipping charge.
                </p>
            </div>

            <Card title="Free shipping & COD">
                <div className="grid md:grid-cols-2 gap-4">
                    <Toggle
                        label="Enable free shipping above order threshold"
                        value={!!s.free_shipping_enabled}
                        onChange={(v) => set("free_shipping_enabled", v)}
                        testId="ship-free-enabled"
                        hint="Rod and heavy carts are excluded — fixed logistics cost."
                    />
                    <NumField
                        label="Free shipping threshold (Rs.)"
                        value={s.free_shipping_threshold}
                        onChange={(v) => set("free_shipping_threshold", v)}
                        testId="ship-free-threshold"
                        disabled={!s.free_shipping_enabled}
                    />
                </div>
                <label className="inline-flex items-center gap-2 text-sm mt-4">
                    <input type="checkbox" checked={!!s.cod_enabled} onChange={(e) => set("cod_enabled", e.target.checked)} className="accent-[#00BCE5]" data-testid="ship-cod" />
                    Enable Cash on Delivery
                </label>
            </Card>

            <Card title="Standard products (by chargeable weight)">
                <div className="grid md:grid-cols-2 gap-4">
                    <NumField label="0 – 500 g charge (Rs.)" value={s.ship_std_0_500_g} onChange={(v) => set("ship_std_0_500_g", v)} testId="ship-std-1" />
                    <NumField label="501 g – 1 kg charge (Rs.)" value={s.ship_std_501_1000_g} onChange={(v) => set("ship_std_501_1000_g", v)} testId="ship-std-2" />
                    <NumField label="1 – 2 kg charge (Rs.)" value={s.ship_std_1001_2000_g} onChange={(v) => set("ship_std_1001_2000_g", v)} testId="ship-std-3" />
                    <NumField label="Each additional kg above 2 kg (Rs.)" value={s.ship_std_extra_per_kg} onChange={(v) => set("ship_std_extra_per_kg", v)} testId="ship-std-extra" />
                </div>
            </Card>

            <Card title="Rod / oversize products">
                <div className="grid md:grid-cols-2 gap-4">
                    <NumField label="Rod base charge (Rs.)" value={s.ship_rod_base} onChange={(v) => set("ship_rod_base", v)} testId="ship-rod-base" />
                    <NumField label="Each kg above 2 kg (Rs.)" value={s.ship_rod_extra_per_kg} onChange={(v) => set("ship_rod_extra_per_kg", v)} testId="ship-rod-extra" />
                    <NumField label="Length above 120 cm surcharge (Rs.)" value={s.ship_rod_len_120} onChange={(v) => set("ship_rod_len_120", v)} testId="ship-rod-120" />
                    <NumField label="Length above 180 cm extra surcharge (Rs.)" value={s.ship_rod_len_180_extra} onChange={(v) => set("ship_rod_len_180_extra", v)} testId="ship-rod-180" />
                </div>
            </Card>

            <Card title="Heavy / bulky products">
                <div className="grid md:grid-cols-2 gap-4">
                    <NumField label="Heavy base charge (Rs.)" value={s.ship_heavy_base} onChange={(v) => set("ship_heavy_base", v)} testId="ship-heavy-base" />
                    <NumField label="Each kg above 2 kg (Rs.)" value={s.ship_heavy_extra_per_kg} onChange={(v) => set("ship_heavy_extra_per_kg", v)} testId="ship-heavy-extra" />
                </div>
            </Card>

            <Card title="General">
                <div className="grid md:grid-cols-3 gap-4">
                    <NumField label="Volumetric divisor (cm³ → kg)" value={s.ship_volumetric_divisor} onChange={(v) => set("ship_volumetric_divisor", v)} testId="ship-vol-div" hint="Default 5000. Lower = costlier oversize." />
                    <NumField label="Fallback weight if missing (g)" value={s.ship_fallback_weight_g} onChange={(v) => set("ship_fallback_weight_g", v)} testId="ship-fallback-weight" hint="Used when a product has no weight." />
                    <NumField label="Fallback shipping floor (Rs.)" value={s.ship_fallback_charge} onChange={(v) => set("ship_fallback_charge", v)} testId="ship-fallback-charge" hint="Minimum shipping for standard carts containing items missing weight." />
                </div>
            </Card>

            {error && (
                <div className="text-sm text-[#FF6E66] bg-[#FF3B30]/10 border border-[#FF3B30]/40 rounded-sm px-4 py-2.5" data-testid="ship-save-error">
                    {error}
                </div>
            )}

            <div className="flex flex-wrap items-center gap-2">
                <button
                    disabled={saving}
                    onClick={save}
                    className="inline-flex items-center gap-2 bg-[#00BCE5] text-black font-display font-bold uppercase tracking-widest px-5 py-3 rounded-sm hover:bg-[#33C9EA] disabled:opacity-50"
                    data-testid="ship-save"
                >
                    <Save size={16} /> {saving ? "Saving…" : "Save shipping rules"}
                </button>
                <button
                    disabled={resetting}
                    onClick={resetDefaults}
                    className="inline-flex items-center gap-2 border border-[#27272A] hover:border-[#FF9F0A] hover:text-[#FF9F0A] text-white/85 font-display font-bold uppercase tracking-widest px-4 py-3 rounded-sm disabled:opacity-50"
                    data-testid="ship-reset"
                >
                    <RotateCcw size={14} /> {resetting ? "Resetting…" : "Reset to defaults"}
                </button>
                <span className="ml-1 text-[11px] text-white/45 inline-flex items-center gap-1.5">
                    <Info size={12} />
                    Existing orders keep their original shipping charge.
                </span>
            </div>

            <Card title="Shiprocket integration (deferred)">
                <div className="text-sm text-white/65 space-y-2">
                    <p>The shipping data layer (weight, dims, COD/prepaid, pincode) is already shaped for Shiprocket. Once API credentials are added, real-time rates will replace these rules and these become the safe fallback.</p>
                    <p className="text-[#00BCE5]">Status: <span className="font-display uppercase tracking-widest font-bold">Pending API credentials</span></p>
                </div>
            </Card>

            <style>{`
                .ost-input {
                    width: 100%;
                    background: #0A0A0B;
                    border: 1px solid #27272A;
                    border-radius: 4px;
                    padding: 10px 12px;
                    color: white;
                    outline: none;
                }
                .ost-input:focus { border-color: #00BCE5; }
                .ost-input:disabled { opacity: 0.5; }
            `}</style>
        </div>
    );
}

function Card({ title, children }) {
    return (
        <section className="bg-[#18181B] border border-[#27272A] rounded-sm">
            <div className="px-4 py-3 border-b border-[#27272A] font-display uppercase font-black tracking-tight">{title}</div>
            <div className="p-4 space-y-2">{children}</div>
        </section>
    );
}

function NumField({ label, value, onChange, testId, hint, disabled }) {
    return (
        <div>
            <label className="block text-[11px] uppercase tracking-widest font-display font-bold text-white/55 mb-1.5">{label}</label>
            <input
                type="number"
                min="0"
                value={value ?? ""}
                onChange={(e) => onChange(e.target.value)}
                className="ost-input"
                disabled={disabled}
                data-testid={testId}
            />
            {hint && <p className="text-[10px] text-white/45 mt-1">{hint}</p>}
        </div>
    );
}

function Toggle({ label, value, onChange, testId, hint }) {
    return (
        <div>
            <label className="flex items-center gap-2.5 cursor-pointer text-sm mt-2">
                <input
                    type="checkbox"
                    checked={!!value}
                    onChange={(e) => onChange(e.target.checked)}
                    className="accent-[#00BCE5] w-4 h-4"
                    data-testid={testId}
                />
                <span>{label}</span>
            </label>
            {hint && <p className="text-[10px] text-white/45 mt-1 ml-6">{hint}</p>}
        </div>
    );
}
